import { FC } from 'react'
import { Button, Stack } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'

import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { ScreenSize } from '../../../utils/constants'
import { imageLoader } from '../../../utils/images'
import { AnalyticsSubLocation } from '../../../utils/types/analytics'
import useIsOnMobile from '../../../services/useIsOnMobile'
import useRFQModal from '../../Modals/hooks/useRFQModal'
import useGetAnalyticsLocation from '../../../services/analytics/useGetAnalyticsLocation'
import useContactModal from '../../Modals/hooks/useContactModal'

import styles from '../../../styles/RfqBanner.module.less'
import buttonStyles from '../../../styles/CustomButtons.module.less'

interface RfqBannerProps {
  forCatalog?: boolean
}

const RfqBanner: FC<RfqBannerProps> = ({ forCatalog }) => {
  const { t } = useTranslation('products')

  const { locale } = useRouter()

  const isSM = useIsOnMobile(ScreenSize.sm)
  const isXL = useIsOnMobile(ScreenSize.xl)

  const analyticsLocation = useGetAnalyticsLocation(AnalyticsSubLocation.Banner)
  const openRfqModal = useRFQModal({ analyticsLocation })
  const openContactModal = useContactModal(undefined, analyticsLocation)

  const getImagePath = () => {
    const prefix = 'rfq-empty-state-banner-'

    if (isXL) return `${prefix}mobile-version.webp`

    const { locale: lang } = getCountryAndLocaleStrings(locale)
    return `${prefix}desktop-version-${lang}.webp`
  }

  const getImageSize = () => {
    if (isSM) return ({ width: 125, height: 105 })
    if (isXL) return ({ width: 227, height: 187 })

    return { width: 494, height: 200 }
  }

  const buttonSize = isSM ? 'md' : 'lg'

  return (
    <Stack
      spacing={10}
      className={`${styles['rfq-banner']} ${forCatalog ? styles['for-catalog'] : ''} padding-top-spacer-double padding-bottom-spacer-double`}
      justifyContent="space-between"
    >
      <Stack.Item className={styles['rfq-banner-content']}>
        <h2 className="margin-bottom-spacer">
          {t('Request a quote')}
        </h2>
        <p className="margin-bottom-spacer-double">
          {t('List the products you’re looking for, and our AI will find the best options and prices for you hassle-free')}
        </p>
        <Stack
          spacing={10}
          justifyContent="space-between"
        >
          <Stack
            wrap
            spacing={10}
            className={styles['buttons-container']}
            alignItems="flex-start"
          >
            <Button
              onClick={() => openRfqModal()}
              size={buttonSize}
              appearance="primary"
              className={`${buttonStyles['custom-button']} ${styles['rfq-button']}`}
              block={isSM}
            >
              {t('modals:Request quote')}
            </Button>
            <Button
              onClick={() => openContactModal()}
              size={buttonSize}
              appearance="ghost"
              className={styles['contact-button']}
              block={isSM}
            >
              {t('common:Contact us')}
            </Button>
          </Stack>
          {isSM && (
            <Stack.Item
              className={styles['image-wrapper']}
              alignSelf="flex-end"
            >
              <Image
                alt={`${t('modals:Request a quote')} banner`}
                loader={imageLoader}
                layout={isXL ? 'intrinsic' : 'fixed'}
                src={getImagePath()}
                {...getImageSize()}
              />
            </Stack.Item>
          )}
        </Stack>
      </Stack.Item>
      {!isSM && (
        <Stack.Item
          className={styles['image-wrapper']}
          alignSelf="flex-end"
        >
          <Image
            alt={`${t('modals:Request a quote')} banner`}
            loader={imageLoader}
            layout={isXL ? 'intrinsic' : 'fixed'}
            src={getImagePath()}
            {...getImageSize()}
          />
        </Stack.Item>
      )}
    </Stack>
  )
}

export default RfqBanner
